import React, { useEffect, useMemo } from 'react';
import Map from '../Map';
import Progress from '../Progress';
import { useSelector } from 'react-redux';
import { TStore, useAppDispatch } from '../../store';
import { searchStrain } from '../../slices/strainDataSlice';
import { searchRecovery } from '../../slices/recoveryDataSlice';
import { Button } from 'antd';
import { Link } from 'react-router-dom';

const Dashboard = ({ vesselPositionData }: any) => {
  const dispatch = useAppDispatch();
  const { searchResults: strainSearchResults } = useSelector((state: TStore) => state.strain);
  const { searchResults: recoverySearchResults } = useSelector((state: TStore) => state.recovery);
  const selectedVessel = useSelector((state: TStore) => state.averageFleetDetails.selectedVessel);
  
  // Memoize date calculations to prevent recalculation on every render
  const { yesterday, dayBefore } = useMemo(() => {
    const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-CA');
    const dayBefore = new Date(Date.now() - 172800000).toLocaleDateString('en-CA');
    return { yesterday, dayBefore };
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (selectedVessel?.shipId) {
      dispatch(searchStrain({ yesterday, shipId: selectedVessel.shipId }));
      dispatch(searchRecovery({ yesterday, shipId: selectedVessel.shipId }));
    }
  }, [dispatch, yesterday, selectedVessel?.shipId]);

  // Fallback data fetch (moved to separate useEffect with proper dependencies)
  useEffect(() => {
    if (selectedVessel?.shipId && 
        (strainSearchResults?.length === 0 || recoverySearchResults?.length === 0)) {
      dispatch(searchStrain({ yesterday: dayBefore, shipId: selectedVessel.shipId }));
      dispatch(searchRecovery({ yesterday: dayBefore, shipId: selectedVessel.shipId }));
    }
  }, [dispatch, dayBefore, selectedVessel?.shipId, strainSearchResults?.length, recoverySearchResults?.length]);

  return (
    <section className="dashboard">
      {/* Crew Map - spans 2x2 grid (rows 1-2, cols 1-2) */}
      <div className="dashboard__card__map">
        <div className="dashboard__title">Crew Condition</div>
        <Map resultSet={vesselPositionData?.[0]} />
      </div>

      {/* Strain Card - row 1, col 3 */}
      <div className="dashboard__card dashboard__card--sleep">
        <div className="title__container">
          <div className="dashboard__title">Average Sleep</div>
          <Button className="action-button">
            <Link to="/sleep">Further Insights &rarr;</Link>
          </Button>
        </div>
        <Progress 
          resultSet={selectedVessel?.averageSleep ?? 0} 
          green={40} 
          orange={40} 
          red={20} 
          max={100} 
        />
      </div>

      {/* Recovery Card - row 2, col 3 */}
      <div className="dashboard__card dashboard__card--recovery">
        <div className="title__container">
          <div className="dashboard__title">Average Recovery</div>
          <Button className="action-button">
            <Link to="/recovery">Further Insights &rarr;</Link>
          </Button>
        </div>
        <Progress 
          resultSet={selectedVessel?.averageRecovery ?? 0} 
          green={40} 
          orange={40} 
          red={20} 
          max={100} 
        />
      </div>

      {/* Sleep Card - row 3, col 3 */}
      <div className="dashboard__card dashboard__card--strain">
        <div className="title__container">
          <div className="dashboard__title">Average Strain</div>
          <Button className="action-button">
            <Link to="/strain">Further Insights &rarr;</Link>
          </Button>
        </div>
        <Progress 
          resultSet={selectedVessel?.averageStrain ?? 0} 
          green={10} 
          orange={5} 
          red={5} 
          max={20} 
        />
      </div>

      {/* Last Updated - row 3, col 1 */}
      <div className="dashboard__card dashboard__card--last-updated">
        <div className="dashboard__title">Last Updated At</div>
        <div className="statistic">
          {strainSearchResults?.length ? 
            strainSearchResults[0].date.toString().split("T")[0] : 
            'No data'
          }
        </div>
      </div>
      
      {/* Number of Crew - row 3, col 2 */}
      <div className="dashboard__card dashboard__card--crew-count">
        <div className="dashboard__title">Number Of Crew Data</div>
        <div className="statistic">
          {strainSearchResults?.length ?? 0}
        </div>
      </div>
    </section>
  );
};

export default Dashboard;